import { useState, type ReactElement } from 'react';
import { Button, Group, Modal, Progress, Stack, Text, Tooltip } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import type { DataObject } from '@/api/objects';
import { isTerminal, useIntakeTypes, useMarkReady } from '@/api/runs';
import { LaunchRunModal } from '@/features/runs/LaunchRunModal';
import { brand } from '@/theme/theme';

/** Imported already processed from another system (P15): there is no upload here to re-run from. */
function processedElsewhere(o: DataObject) {
  return o.origin === 'referenced' && !!o.processed_uri && o.processed_uri === o.raw_uri;
}

/**
 * Which intake action an object can take next (project-lifecycle.md §3).
 *
 * `hasType` is undefined while forun's intake types for this object are still unknown; until
 * then neither offer is made, so a slow lookup can never present readying as the only choice.
 */
export function intakeOptions(o: DataObject, hasType: boolean | undefined) {
  return {
    preprocess: o.state === 'verified' && hasType === true,
    markReady: o.state === 'verified' && hasType === false,
    rerun: o.state === 'ready' && hasType === true && !o.raw_deleted && !processedElsewhere(o),
    inFlight: o.state === 'preprocessing',
  };
}

export function ObjectIntakeActions({ object, projectActive }: { object: DataObject; projectActive: boolean }) {
  const { t } = useTranslation('runs');
  const { t: tc } = useTranslation();
  const [launch, setLaunch] = useState(false);
  const [confirmReady, setConfirmReady] = useState(false);
  const types = useIntakeTypes(object.public_id);
  const markReady = useMarkReady(object.public_id);
  const hasType = types.data === undefined ? undefined : !!types.data.preprocess;
  const opt = intakeOptions(object, hasType);
  const disabledAll = object.deleted || !projectActive;
  const latest = object.latest_run;
  const awaitingTypes = hasType === undefined && (object.state === 'verified' || object.state === 'ready');

  const reason = disabledAll
    ? t(object.deleted ? 'intake.deleted' : 'intake.projectClosed')
    : opt.inFlight
      ? t('intake.inFlight')
      : awaitingTypes
        ? t('intake.typesLoading')
        : object.state === 'ready' && object.raw_deleted
          ? t('intake.rawDeleted')
          : object.state === 'ready' && processedElsewhere(object)
            ? t('intake.processedElsewhere')
            : t('intake.notApplicable', { state: object.state });
  const wrap = (enabled: boolean, b: ReactElement) => (enabled && !disabledAll ? b : <Tooltip label={reason}>{b}</Tooltip>);

  return (
    <Group gap="sm" align="center">
      {wrap(
        opt.preprocess,
        <Button size="xs" disabled={!opt.preprocess || disabledAll} loading={awaitingTypes && !disabledAll && object.state === 'verified'} onClick={() => setLaunch(true)}>
          {t('intake.preprocess')}
        </Button>,
      )}
      {wrap(
        opt.rerun,
        <Button size="xs" variant="default" disabled={!opt.rerun || disabledAll} onClick={() => setLaunch(true)}>
          {t('intake.rerun')}
        </Button>,
      )}
      {opt.markReady && (
        <Tooltip label={disabledAll ? reason : t('intake.noType')}>
          <Button size="xs" variant="default" disabled={disabledAll} loading={markReady.isPending} onClick={() => setConfirmReady(true)}>
            {t('intake.markReady')}
          </Button>
        </Tooltip>
      )}
      {latest && !isTerminal(latest.state) && (
        <Group gap="xs" ml="sm">
          <Text fz={12} c={brand.textMuted}>
            {latest.type} · {latest.state}
          </Text>
          <Progress w={120} value={Math.round((latest.progress ?? 0) * 100)} color="forest.6" bg="forest.0" size="sm" radius="sm" />
        </Group>
      )}
      {launch && <LaunchRunModal object={object} type="preprocess" rerun={opt.rerun} opened onClose={() => setLaunch(false)} />}
      <Modal opened={confirmReady} onClose={() => setConfirmReady(false)} title={t('intake.markReadyConfirmTitle')}>
        <Stack gap="md">
          <Text fz={14}>{t('intake.markReadyConfirm')}</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmReady(false)}>
              {tc('common.cancel')}
            </Button>
            <Button
              loading={markReady.isPending}
              onClick={() => markReady.mutate(undefined, { onSettled: () => setConfirmReady(false) })}
            >
              {t('intake.markReady')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Group>
  );
}
